/**
 * PLAT-9968 — Canva-style floating action menu over the clip-preview canvas.
 *
 * Renders as an overlay above the Creatomate iframe when an intro card,
 * end card, or namecard composition is selected. Three actions:
 *
 *   • Edit       — opens the existing `CompositionQuickEditPopover`
 *   • Duplicate  — clones the placement (caller-supplied; optional)
 *   • Delete     — removes the placement from the clip's timeline
 *
 * Visual: pill-shaped white surface with rounded ends, icon buttons inside,
 * positioned absolutely at the top-center of the canvas. Mirrors the Canva
 * floating toolbar pattern referenced in the PLAT-9968 scope addition.
 *
 * Accessibility:
 *   • `role="toolbar"` with `aria-label`
 *   • Per-button `aria-label`s ("Edit composition" etc.)
 *   • Each button reachable with Tab; Enter/Space activates
 *   • Escape closes (caller clears the selection)
 *   • Tooltips bound by `aria-describedby` via react-tooltip
 *
 * Pointer-events policy: the menu sits inside a `pointer-events: none`
 * overlay layer so it never blocks clicks on the iframe; the buttons
 * themselves opt back into `pointer-events: auto`.
 */
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import AdjustRangeIcon from '@src/svgs/AdjustRangeIcon';
import CopyIcon from '@src/svgs/CopyIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import PencilIcon from '@src/svgs/PencilIcon';
import React, { useCallback, useId, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export interface CompositionCanvasActionMenuProps {
  /**
   * Anchor element that the popover hangs off when Edit is clicked. The
   * caller passes its menu wrapper ref so the popover anchors directly to
   * the menu (mirroring Canva's pattern where the editor panel opens
   * adjacent to the toolbar). Optional — when omitted, `onEdit` is called
   * without an anchor and the caller is responsible for positioning.
   */
  onEdit: (anchorElement: HTMLElement) => void;
  /**
   * Optional disabled-state for the Edit button. When set, the button
   * renders disabled with `editDisabledReason` as its tooltip — useful
   * when the selected composition has no `allowQuickEdit: true` controls
   * authored in DatoCMS and there's nothing to edit. Delete + Duplicate
   * stay enabled.
   */
  editDisabled?: boolean;
  /** Tooltip + aria-description shown when Edit is disabled. */
  editDisabledReason?: string;
  /**
   * Called when Delete is clicked. Caller confirms (if needed) and runs
   * the actual timeline mutation. Returning `false` (or rejecting) cancels
   * the close-on-action behaviour; otherwise the parent should clear its
   * own active-element selection.
   */
  onDelete: () => void;
  /**
   * Optional Duplicate handler. When omitted, the Duplicate button is
   * hidden. Surfaces that don't yet have a clean duplicate path (e.g.
   * v1 clip preview — system-managed namecards) should leave this out
   * rather than ship a broken button.
   */
  onDuplicate?: () => void;
  /**
   * Optional "Adjust range" handler. When provided, an extra button is
   * shown that turns on the transcript drag-handles for re-anchoring an
   * overlay's start/end words. Omitted on surfaces / element types that
   * don't support range adjustment (cards, namecards, translated view).
   */
  onAdjustRange?: () => void;
  /** Tooltip + aria-label for the Adjust-range button. */
  adjustRangeTooltip?: string;
  /**
   * Label of the composition type, surfaced to screen readers as the
   * toolbar's accessible name (e.g. "Intro card actions").
   */
  ariaLabel: string;
  /**
   * Tooltip + aria-label for the Edit pencil. Required so the visible
   * hint and the screen-reader name agree — callers pass a
   * composition-aware label like "Edit Graphic Overlay — Smart Power".
   */
  editTooltip: string;
  /**
   * PLAT-10973 — suppress the Edit pencil's tooltip while the quick-edit
   * popover it opens is on screen.
   *
   * Clicking the pencil leaves the cursor sitting on it, so react-tooltip keeps
   * the hover tooltip up and it collides with the popover that just opened —
   * two overlapping labels for the same graphic. The pencil's `aria-label` is
   * unaffected, so the accessible name survives the suppression. Delete /
   * Duplicate / Adjust keep their tooltips: they stay hoverable while the
   * popover is open and nothing overlaps them.
   */
  editTooltipSuppressed?: boolean;
}

export const CompositionCanvasActionMenu: React.FC<
  CompositionCanvasActionMenuProps
> = ({
  onEdit,
  onDelete,
  onDuplicate,
  onAdjustRange,
  adjustRangeTooltip = 'Adjust transcript range',
  ariaLabel,
  editDisabled = false,
  editDisabledReason,
  editTooltip,
  editTooltipSuppressed = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const editTooltipId = useId();
  const adjustTooltipId = useId();
  const duplicateTooltipId = useId();
  const deleteTooltipId = useId();

  const handleEditClick = useCallback(() => {
    const el = wrapperRef.current;
    if (el) onEdit(el);
  }, [onEdit]);

  return (
    <Pill
      ref={wrapperRef}
      role="toolbar"
      aria-label={ariaLabel}
      data-testid="composition-canvas-action-menu"
      onMouseDown={e => e.stopPropagation()}
    >
      <IconButton
        type="button"
        onClick={handleEditClick}
        disabled={editDisabled}
        aria-label={editTooltip}
        // Detaching the anchor AND unmounting the tooltip below: react-tooltip
        // resolves its anchor from this attribute, so dropping it stops a new
        // hover opening one, and unmounting removes any instance already on
        // screen from the click that opened the popover.
        data-tooltip-id={editTooltipSuppressed ? undefined : editTooltipId}
        data-tooltip-content={
          editDisabled
            ? (editDisabledReason ?? 'No editable text fields')
            : editTooltip
        }
        data-testid="composition-canvas-action-edit"
      >
        <PencilIcon
          width="14"
          height="14"
          strokeColor="var(--popover-foreground)"
        />
      </IconButton>
      {!editTooltipSuppressed && (
        <Tooltip id={editTooltipId} style={COMPACT_TOOLTIP_STYLE} />
      )}

      {onAdjustRange && (
        <>
          <IconButton
            type="button"
            onClick={onAdjustRange}
            aria-label={adjustRangeTooltip}
            data-tooltip-id={adjustTooltipId}
            data-tooltip-content={adjustRangeTooltip}
            data-testid="composition-canvas-action-adjust-range"
          >
            <AdjustRangeIcon
              width="16"
              height="16"
              strokeColor="var(--popover-foreground)"
            />
          </IconButton>
          <Tooltip id={adjustTooltipId} style={COMPACT_TOOLTIP_STYLE} />
        </>
      )}

      {onDuplicate && (
        <>
          <IconButton
            type="button"
            onClick={onDuplicate}
            aria-label="Duplicate composition"
            data-tooltip-id={duplicateTooltipId}
            data-tooltip-content="Duplicate"
            data-testid="composition-canvas-action-duplicate"
          >
            <CopyIcon
              width="14"
              height="14"
              strokeColor="var(--popover-foreground)"
            />
          </IconButton>
          <Tooltip id={duplicateTooltipId} style={COMPACT_TOOLTIP_STYLE} />
        </>
      )}

      <IconButton
        type="button"
        onClick={onDelete}
        aria-label="Delete composition"
        data-tooltip-id={deleteTooltipId}
        data-tooltip-content="Delete"
        data-testid="composition-canvas-action-delete"
        $danger
      >
        <DeleteIcon
          width="14"
          height="14"
          strokeColor="var(--popover-foreground)"
        />
      </IconButton>
      <Tooltip id={deleteTooltipId} style={COMPACT_TOOLTIP_STYLE} />
    </Pill>
  );
};

const Pill = styled.div`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: 999px;
  box-shadow: var(--elevation-toast);
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 999px;
  cursor: pointer;
  color: var(--popover-foreground);
  transition:
    background 0.12s ease,
    color 0.12s ease;

  &:hover {
    background: ${p =>
      p.$danger
        ? 'color-mix(in srgb, var(--destructive) 18%, transparent)'
        : 'var(--primary-tint-light)'};
    color: ${p => (p.$danger ? 'var(--destructive)' : 'inherit')};
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
